// Multi-origin configuration (ADR-015; platform target model §1.6): the one fact every config command records, the
// config entities and the authority of each of their fields. Catalog, pricing and settings commands at the node or in
// the cloud stamp each changed field `(value, hlc, origin, basis, commandId)` and record the stamps as a
// `ConfigFieldsWritten` fact of their own origin, so config replicates over the same push/pull as every other fact and
// every replica merges it per field by HLC (`@radish/sync` config merge).
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import type { Origin } from '../../define/meta.ts';
import { settings } from '../../settings/registry.ts';
import { FieldStamp, Tombstone } from '../../sync/protocol.ts';

/**
 * The payload names its entity as an open string (v13, ops plan 2026-10-12-merge-hot-spots), so a new config entity or
 * a new field never changes this event's schema and never needs a version bump. The registry decides which entities
 * exist: the product's config writes refuse an entity `CONFIG_ENTITIES` does not hold (`isConfigEntity`), and a replica
 * built before an entity existed decodes its facts and holds them without projecting them.
 */
const ConfigWritePayload = z.object({
  entity: z.string().min(1).max(100),
  id: z.string().min(1).max(200),
  stamps: z.record(z.string().min(1).max(100), FieldStamp),
  tombstone: Tombstone.optional(),
});

export const ConfigFieldsWritten = defineEvent({
  name: 'ConfigFieldsWritten',
  // v2-v12 each added config entities to a closed list (v2 break policies ... v11 the online store, v12 customers).
  // v13 opened the entity name to a string checked by the registry at write, the last bump for a new entity: add the
  // entity to its context's field map instead. Every older payload names an entity that is still a string, so it parses
  // unchanged.
  version: 13,
  summary: "Fields of one config entity written at one origin, each with its HLC stamp, or the entity's tombstone.",
  aggregate: 'config',
  payload: ConfigWritePayload,
  verifiable: ['entity', 'id', 'stamps', 'tombstone'],
  merge: 'commutative',
  deviceConsumed: true,
  // v1-v5 and v7-v12 only widened the entity name, so each reads as the next version unchanged.
  upcastFrom: [
    additive(1), additive(2), additive(3), additive(4), additive(5), { version: 6, up: (old) => ConfigWritePayload.parse(old) }, additive(7), additive(8),
    additive(9), additive(10), additive(11), additive(12),
  ],
});

/** A config entity's field: where it may be written from, and whether a store may override an account-shared value. */
export interface ConfigField {
  readonly origin: Origin;
  readonly overridable: boolean;
}

const any = { origin: 'any', overridable: false } as const satisfies ConfigField;
const overridable = { origin: 'any', overridable: true } as const satisfies ConfigField;
/** Online publishing is a cloud command (catalog.publishProductOnline, catalog-outline). */
const cloud = { origin: 'cloud', overridable: false } as const satisfies ConfigField;
/** Written at a register too (offline, as its own fact), besides the node and the cloud. */
const device = { origin: 'device', overridable: false } as const satisfies ConfigField;
/** Written at a register too, and a store's own on an account-shared entity (the 86 switch). */
const deviceOverridable = { origin: 'device', overridable: true } as const satisfies ConfigField;

/**
 * Per-field authority of the catalog entities. Price, visibility on the till and the till name are the fields a store
 * may override on an account-shared product (PTM §7.3); identity, structure and tax treatment are not. A store's
 * override is a field of the same entity, `override:<locationId>:<field>`, so it merges per field like every other and
 * a store's edit never contends with the account value it overrides.
 */
export const CATALOG_FIELDS = {
  'catalog.product': {
    locationId: any, name: any, posName: overridable, categoryId: any, price: overridable, taxTreatment: any, taxProfileId: any,
    visibleOnPos: overridable, visibleOnline: cloud, archived: any, onSpeedScreen: overridable, posSortKey: any,
    itemKind: any, unitLabel: any, kitchenName: overridable, receiptName: overridable, onlineName: cloud, description: any, departmentId: any,
    courseId: any, ageRestrictionId: any, displayCategoryIds: any, speedScreenSortKey: overridable, taxExemptionIds: any, modifierGroups: any,
    // 86 is per store (target model §7.4): a register at one location never takes an item off another's menu. A register
    // 86es offline as its own fact (W-74; coordinator decision 2026-10-12), so the switch has device authority.
    available: deviceOverridable,
    color: any, sku: any, onlineSortKey: any,
    // The colour's name (B1066) is the cloud's until every node declares it: a node on the build before reads an
    // undeclared field as cloud-authoritative and quarantines a node's stamp of it (plan step 0). Widen it to `any` with
    // the node floor, never before.
    colorName: cloud,
    // Kitchen routing: printers belong to a store, so a shared product's printers are the store's to override.
    includedPrinterIds: overridable, excludedPrinterIds: overridable,
  },
  'catalog.variant': { locationId: any, productId: any, name: any, price: overridable, sku: any, sortKey: any, isDefault: any },
  'catalog.modifier_group': {
    locationId: any, name: any, posTitle: any, onlineTitle: cloud, showNameOnKitchenTicket: any, required: any, minimum: any, maximumUnique: any,
    maximumQuantity: any, allowMultipleQuantity: any, quantitySelector: any, defaultQuantity: any, conversational: any, conversationButtons: any,
    conversationReset: any, splittable: any, splitText: any, options: any,
  },
  'catalog.department': { locationId: any, name: any, parentId: any },
  'catalog.course': { locationId: any, name: any, sortKey: any },
  'catalog.dining_option': { locationId: any, name: any, isDefault: any, sortKey: any },
  'catalog.age_restriction': { locationId: any, name: any, message: any, minimumAge: any, sortKey: any },
  'catalog.tax_exemption': { locationId: any, name: any, diningOptionIds: any },
  'catalog.void_reason': { locationId: any, reason: any },
  'catalog.tender_type': { locationId: any, name: any, allowExtraDetails: any, detailTitle: any },
  'catalog.category': {
    // colorName: the cloud's until the node floor moves, as on the product.
    locationId: any, name: any, color: any, colorName: cloud, parentId: any, visibleOnPos: any, visibleOnline: cloud, posSortKey: any, kitchenSortKey: any, onlineSortKey: any, printerIds: any,
  },
  'catalog.printer': { locationId: any, name: any, ip: any, kind: any, itemize: any, preselectOnMiscItems: any },
  'catalog.tax_profile': { locationId: any, name: any, rate: any, isLocationDefault: any },
  'catalog.discount_rule': {
    locationId: any, name: any, published: any, application: any, adjustmentKind: any, discountScope: any, value: any, targets: any,
    schedules: any, combinability: any,
  },
} as const satisfies Record<string, Record<string, ConfigField>>;

/** Per-field authority of the floor config entities (lane D3): written at the node or in the cloud. */
export const FLOOR_FIELDS = {
  'floor.floor_plan': { locationId: any, name: any, sectionCount: any, color: any, enabled: any, planState: any, sortKey: any, items: any },
  'floor.zone': { locationId: any, prefix: any, label: any },
  'floor.free_table': { locationId: any, zoneId: any, tableNumber: any },
  // The API Keys tab (admin and reseller) and the subdomain are the cloud's, and so is Voice Ordering (the voice worker
  // runs beside the cloud storefront); a register pauses the store and picks the
  // default prep time itself (offline, its own fact).
  'online.store': {
    locationId: any, displayName: any, businessDescription: any, address: any, city: any, state: any, zipcode: any, contactFirstName: any,
    contactLastName: any, email: any, phone: any, subdomain: cloud, acceptingOrders: device, snoozed: any, snoozedUntil: any, snoozedMinutes: any,
    catalogMode: any, orderApproval: any, printerIds: any, menuHours: any, businessHours: any, specialClosures: any, prepTimes: device, banners: any,
    notices: any, gateway: cloud, merchantId: cloud, gatewayPublicKey: cloud, gatewayPrivateKeyLast4: cloud, gatewayPrivateKeySetAt: cloud,
    voice: cloud,
  },
  // QR Table Pay's codes (SCR-106): written in the cloud only (online.upsertQrCode and deleteQrCode are cloud-only, and the
  // cloud refuses a node's fact on one), since a code's text decides where every printed copy opens. The text and the
  // legacy hashid are cloud fields, so a node-stamped value never wins the merge either.
  'online.qr_code': { locationId: any, name: any, table: any, backgroundColor: any, foregroundColor: any, margin: any, url: cloud, legacyHashid: cloud },
} as const satisfies Record<string, Record<string, ConfigField>>;

/** A customer profile (contexts/customers) is written at the node (a register's create, offline too) or in the cloud. */
export const CUSTOMER_FIELDS = {
  // `houseAccountHistory` (lane D2b): the customer's house-account charges and payments from legacy, written once by the
  // import in the cloud and read with the live ones; nothing at a store writes it.
  'customers.customer': { locationId: any, firstName: any, lastName: any, email: any, phone: any, notes: any, addresses: any, houseAccountHistory: cloud },
} as const satisfies Record<string, Record<string, ConfigField>>;

/** Per-field authority of the labor config entities: a location's break definitions and its staff members (contexts/labor/staff.ts) are written at the node or in the cloud. */
export const LABOR_FIELDS = {
  'labor.break_policy': { locationId: any, name: any, minutes: any, bufferMinutes: any, paid: any, convertOverrun: any, payForBuffer: any, trackPaidOverstay: any },
  // B1623: a break definition's last name, kept after it is retired (its `labor.break_policy` row is then a tombstone), so a
  // register names the breaks planned with it as legacy does (ShiftBreakResource joins employee_breaks withTrashed). Written
  // beside every create, rename and retire (labor.recordBreakPolicyName); an older register ignores the unknown entity.
  'labor.break_policy_name': { locationId: any, name: any },
  'labor.staff_member': {
    locationId: any, name: any, role: any, grants: any, pinVerifier: any, pinLength: any, active: any, firstName: any, lastName: any, jobRoleId: any,
    wage: any, externalEmployeeId: any, startOn: any, endOn: any, portalEnabled: any, isManager: any, contact: any, mealWaivers: any,
    mealPremiumExempt: any,
  },
  'labor.job_role': { locationId: any, name: any, description: any, wage: any, color: any, grants: any, sortKey: any },
  'labor.tip_source': { locationId: any, name: any, description: any },
  'labor.schedule': { locationId: any, weekStartsOn: any, publication: any, publishedAt: any, notes: any },
  'labor.shift': {
    locationId: any, scheduleId: any, employeeId: any, jobRoleId: any, businessDate: any, startTime: any, endTime: any, breaks: any, notes: any, color: any,
    // Open-shift claims (lane D4): decided at the node, like the employee a claim assigns.
    claims: any,
    // Resolve Conflict (lane D4): when and by whom the shift's conflicts were marked resolved (null when not).
    conflictResolvedAt: any, conflictResolvedBy: any,
  },
  // Time off (lane D4): filed, edited and cancelled at a register through the node; reviewed and deleted in the Back Office.
  'labor.time_off_request': {
    locationId: any, employeeId: any, startDate: any, endDate: any, reason: any, status: any, requestedAt: any, createdBy: any, reviewedBy: any, reviewedAt: any,
    reviewerNotes: any,
  },
  // Employee availability (lane D4; id = the employee's id): set in the Back Office, read by the schedule.
  'labor.employee_availability': { locationId: any, employeeId: any, days: any, effectiveFrom: any },
  // Schedule templates (lane D4): saved and deleted in the Back Office.
  'labor.schedule_template': { locationId: any, name: any, description: any, shifts: any, createdAt: any },
} as const satisfies Record<string, Record<string, ConfigField>>;

/**
 * The device registry (platform target model §4.5; ops plan 2026-10-11-device-records): a device record's location,
 * name and form factor, and the keys of the units activated onto it. Activation may happen at the node (the store's own
 * Wi-Fi, internet down) or in the cloud, so each unit's key is its own field, `key:<keyId>` (`deviceKeyField`); a
 * record claimed before device records holds its one key in `publicJwk`/`keyThumbprint`. Revocation (of the record or
 * of one key, `keyRevoked:<keyId>`), billing and the Terminal ID's last 4 come only from the cloud, the authority every
 * node and device hears (doc 11). The Terminal ID itself is never a field. A node verifies device tokens from these
 * replicated rows, offline.
 */
export const FLEET_FIELDS = {
  'fleet.device': {
    locationId: any, name: any, formFactor: any, publicJwk: any, keyThumbprint: any, claimedAt: any, revokedAt: cloud, revokedReason: cloud,
    billable: cloud, terminalIdLast4: cloud, terminalIdSetAt: cloud,
  },
  // A migrating location's device switch-over (D-23; id = the location id): held from its import, so no unit there
  // signs in, then switched over at cutover, releasing every unit at once. The cloud's alone. `drainSince` is set when a
  // hold undoes a switch-over (rollback, `fleet.holdDevicesAgain`): a unit activated before it may then push and heartbeat
  // its outbox, and nothing else, until it has drained (ops plan 2026-10-11-parity-program, lane C, drain gap (b)).
  'fleet.location_cutover': { locationId: cloud, heldAt: cloud, switchedAt: cloud, drainSince: cloud },
  // Reader-gated features (WF-40 round 3; contracts sync/capabilities.ts; id = the location id): the features every
  // active register reads, as the node saw its registers' pulls (`atNode`) and as the cloud saw those on its route
  // (`atCloud`). Only the node's code writes `atNode`.
  'fleet.location_capabilities': { locationId: any, atNode: any, atCloud: cloud },
  // Reviewed post-freeze sales discarded after a rollback (id = the location id; `fleet.discardDrainHolds`): one field
  // per command, `discard:<commandId>` (`drainDiscardField`), cloud-authoritative like every undeclared field.
  'fleet.location_drain_discards': { locationId: cloud },
  // A160 step 4 (b): the cloud's order-number count handed to the location's hub (id = the location id;
  // `fleet.handOverOrderNumbering`). The cloud's alone; the hub raises its carry-over floor to `lastNumber`.
  'fleet.location_order_numbering': { locationId: cloud, businessDate: cloud, lastNumber: cloud, nodeId: cloud, handedAt: cloud },
} as const satisfies Record<string, Record<string, ConfigField>>;

/** The field one discarded drain-held command is stored under on its location's `fleet.location_drain_discards`. */
export const drainDiscardField = (commandId: string): string => `discard:${commandId}`;
/** The field one unit's key is stored under on its device record (any origin: a node may activate offline). */
export const deviceKeyField = (keyId: string): string => `key:${keyId}`;
/** The field one unit's revocation is stored under (cloud only). */
export const deviceKeyRevokedField = (keyId: string): string => `keyRevoked:${keyId}`;

/** A location's reporting config (lane D5): its time segments (dayparts), written at the node or in the cloud. */
export const REPORTING_FIELDS = {
  'reporting.time_segment': { locationId: any, name: any, startAt: any, endAt: any },
} as const satisfies Record<string, Record<string, ConfigField>>;

/**
 * The location's in-store card processor account as the node and its registers see it (lane N, native-modules §4.5):
 * the processor, which of its hosts, and the merchant ID the terminal sends (legacy `locations.gateway`,
 * `datacap_gateway_sandbox`, `dc_direct_merchant_id`, which legacy's device status also handed every register). Radish
 * staff and resellers set it in the cloud (`payments.setProcessorAccount`); the Pay API key is sealed beside it in the
 * cloud and is never a field (ADR-009, D7). One record per location, id = the location id.
 */
export const PAYMENT_FIELDS = {
  'payments.processor_account': { locationId: cloud, processor: cloud, environment: cloud, merchantRef: cloud },
} as const satisfies Record<string, Record<string, ConfigField>>;

/**
 * A location's entitlements (ops plan 2026-10-13-billing-system §4.5, slice 6; id = the location id): its plan's name,
 * whether enforcement is on, the features on, the add-ons paused and the upsell offers. The cloud's alone (billing is
 * Stripe, which only the cloud reaches); the node and every register hold the last one synced, so offline they keep it.
 * A build without the entity holds the fact unprojected, and a replica with no row shows everything (fail open).
 */
export const BILLING_FIELDS = {
  'billing.entitlements': { locationId: cloud, planName: cloud, enforcement: cloud, features: cloud, paused: cloud, offers: cloud },
} as const satisfies Record<string, Record<string, ConfigField>>;

/** The setting rows' entities (fields are registry keys), besides the field maps above. */
export const SETTINGS_ENTITIES = ['settings.value', 'settings.effective'] as const;

/**
 * Every config entity (sync `entity` names), one row per id merged field by field. Derived from the field maps, so a
 * new entity is one line in its context's map and touches nothing shared.
 */
export type ConfigEntity =
  | keyof typeof CATALOG_FIELDS | keyof typeof LABOR_FIELDS | keyof typeof FLEET_FIELDS | keyof typeof FLOOR_FIELDS | keyof typeof CUSTOMER_FIELDS
  | keyof typeof REPORTING_FIELDS | keyof typeof PAYMENT_FIELDS | keyof typeof BILLING_FIELDS
  | (typeof SETTINGS_ENTITIES)[number];

export const CONFIG_ENTITIES: readonly ConfigEntity[] = [
  ...SETTINGS_ENTITIES, ...(Object.keys({ ...CATALOG_FIELDS, ...LABOR_FIELDS, ...FLEET_FIELDS, ...FLOOR_FIELDS, ...CUSTOMER_FIELDS, ...REPORTING_FIELDS, ...PAYMENT_FIELDS, ...BILLING_FIELDS }) as ConfigEntity[]),
];

const KNOWN_ENTITIES: ReadonlySet<string> = new Set(CONFIG_ENTITIES);

/** Whether the registry declares `entity`. Every config write checks it; a fact for any other entity is held, not projected. */
export const isConfigEntity = (entity: string): entity is ConfigEntity => KNOWN_ENTITIES.has(entity);

/**
 * Setting rows: `settings.value` and `settings.effective` ids are `<scope_kind>:<scope_id>`. A `settings.value` field is
 * a registry key; a `settings.effective` field is `<key>@<effective business date>`, so two history entries never
 * contend for one field.
 */
export const effectiveField = (key: string, effectiveFrom: string): string => `${key}@${effectiveFrom}`;
export const parseEffectiveField = (field: string): { readonly key: string; readonly effectiveFrom: string } => {
  const at = field.lastIndexOf('@');
  return { key: field.slice(0, Math.max(0, at)), effectiveFrom: field.slice(at + 1) };
};

const OVERRIDE = /^override:([0-9a-f-]{36}):(.+)$/;

/** The field a location's override of `field` is stored under. */
export const overrideField = (locationId: string, field: string): string => `override:${locationId}:${field}`;

export const parseOverrideField = (field: string): { readonly locationId: string; readonly field: string } | null => {
  const m = OVERRIDE.exec(field);
  return m === null ? null : { locationId: m[1] ?? '', field: m[2] ?? '' };
};

/**
 * The value a reverted override is written as (NEW-06). A config field is never removed, only written again, so a
 * store's revert writes this over its override and every reader treats the field as not overridden. No catalog field's
 * wire form is an object with this key, so it cannot be mistaken for a value.
 */
export const REVERTED_OVERRIDE = { revertedToShared: true } as const;

export const isRevertedOverride = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && (value as { revertedToShared?: unknown }).revertedToShared === true;

/** Whether a store may override `field` of an account-shared `entity`. */
export const isOverridable = (entity: string, field: string): boolean =>
  (CATALOG_FIELDS as Record<string, Record<string, ConfigField> | undefined>)[entity]?.[field]?.overridable === true;

/**
 * ADR-015 field authority for every config entity: settings take `origin` from the registry, catalog fields from
 * `CATALOG_FIELDS`. Anything undeclared is cloud-authoritative, so an unknown field can never be written at a node.
 */
export function configFieldAuthority(entity: string, field: string): Origin {
  if (entity === 'settings.value') return (settings as Record<string, { origin: Origin } | undefined>)[field]?.origin ?? 'cloud';
  if (entity === 'settings.effective') return (settings as Record<string, { origin: Origin } | undefined>)[parseEffectiveField(field).key]?.origin ?? 'cloud';
  if (entity === 'fleet.device' && field.startsWith('key:')) return 'any';
  const fields = ({ ...CATALOG_FIELDS, ...LABOR_FIELDS, ...FLEET_FIELDS, ...FLOOR_FIELDS, ...CUSTOMER_FIELDS, ...REPORTING_FIELDS, ...PAYMENT_FIELDS, ...BILLING_FIELDS } as Record<string, Record<string, ConfigField> | undefined>)[entity];
  const override = parseOverrideField(field);
  if (override !== null) return isOverridable(entity, override.field) ? (fields?.[override.field]?.origin ?? 'cloud') : 'cloud';
  return fields?.[field]?.origin ?? 'cloud';
}

export const CloudAuthoritative = defineError({
  code: 'config.cloud_authoritative', httpStatus: 403, message: 'errors.config.cloud_authoritative', retryable: false,
  details: z.object({ entity: z.string(), fields: z.array(z.string()) }),
});

export const ProposalRequired = defineError({
  code: 'config.proposal_required', httpStatus: 409, message: 'errors.config.proposal_required', retryable: false,
  details: z.object({ entity: z.string(), fields: z.array(z.string()) }),
});
